import { useState } from "react";
import Select from "react-select";
import { SlIcon } from "@shoelace-style/shoelace/dist/react";
import API from "../../../services/api";
import { EmitEvent } from "../../../services/socket";
import { Toast } from "../../../shared/Toast";
import { ConfirmationModal } from "../../../shared/ConfirmationModal";
import { formatDateWithTimeYear } from "../../../utils/dateFormat";
import { AD_FORMAT_CHOICES, adFormatLabel } from "../utils/adFormats";

// Pinned, CST-only banner shown at the top of a creative's chat when an ad-type
// change request is pending (creative status === -1). Approving lets the CST pick
// the new ad type (accept_request + change_ad_type); rejecting restores the
// previous status (reject_request) after a confirmation.
const AdTypeRequestBanner = ({ creative, pendingLog, onResolved }) => {
  const creative_id = creative?.creative_id;
  const currentType = creative?.ad_creative_sub_type;

  const [collapsed, setCollapsed] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmReject, setConfirmReject] = useState(false);
  const [busy, setBusy] = useState(false);

  const requestedByName = pendingLog?.activity_done_by_name;
  const requestMessage = pendingLog?.message;
  const requestDate = pendingLog?.date;

  const emitLog = (log) => {
    if (log) EmitEvent(creative_id, log);
  };

  const handleAccept = async () => {
    const value = selected?.value;
    if (!value) return Toast("info", "Please select an ad type");
    setBusy(true);
    try {
      const acc = await API({
        url: "/alpha_cst_designer/accept_request",
        method: "POST",
        data: { creative_id },
      });
      if (acc.status !== "SUCCESS") {
        Toast("error", "Failed to approve request");
        return;
      }
      emitLog(acc.data?.log?.[0]);

      const chg = await API({
        url: "/alpha_cst_designer/change_ad_type",
        method: "POST",
        data: { creative_id, ad_creative_sub_type: value },
      });
      if (chg.status !== "SUCCESS") {
        Toast("error", "Request approved, but failed to change ad type");
        return;
      }
      emitLog(chg.data?.log?.[0]);

      Toast("success", "Ad type request approved");
      onResolved?.();
    } catch (err) {
      Toast("error", err.message || "Failed to approve request");
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    setBusy(true);
    try {
      const creativePayload = {
        ...creative,
        pending_log: creative?.pending_log || pendingLog || { date: requestDate },
      };
      const res = await API({
        url: "/alpha_cst_designer/reject_request",
        method: "POST",
        data: { creative_id, creative: creativePayload },
      });
      if (res.status !== "SUCCESS") {
        Toast("error", "Failed to reject request");
        return;
      }
      emitLog(res.data?.log?.[0]);
      Toast("success", "Ad type request rejected");
      setConfirmReject(false);
      onResolved?.();
    } catch (err) {
      Toast("error", err.message || "Failed to reject request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="absolute top-14 right-2 z-30 flex flex-col items-end">
      {collapsed ? (
        /* Floating pending indicator — keeps the conversation fully visible */
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          title="Ad type change request — tap to review"
          className="relative inline-flex items-center gap-1.5 rounded-full bg-amber-500 text-white pl-2.5 pr-3 py-1.5 shadow-lg text-xs font-semibold hover:bg-amber-600 transition-colors"
        >
          <span className="absolute -inset-0.5 rounded-full bg-amber-400 opacity-50 animate-ping pointer-events-none" />
          <SlIcon library="lucide" name="alert-circle" style={{ fontSize: "14px" }} className="relative" />
          <span className="relative whitespace-nowrap">Ad type request</span>
        </button>
      ) : (
      <div className="w-80 max-w-[calc(100vw-1rem)] rounded-xl border border-amber-300 bg-amber-50 shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-start gap-2 px-3 py-2 bg-amber-100/70 border-b border-amber-200">
          <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-amber-500 text-white">
            <SlIcon library="lucide" name="refresh-cw" style={{ fontSize: "13px" }} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-amber-900 leading-tight">Ad Type Change Request</p>
            <p className="text-[11px] text-amber-700 leading-tight truncate">
              {requestedByName ? `Requested by ${requestedByName}` : "Pending approval"}
              {requestDate ? ` · ${formatDateWithTimeYear(requestDate)}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            title="Minimize"
            className="flex-shrink-0 -mr-1 p-1 text-amber-700 hover:text-amber-900"
          >
            <SlIcon library="lucide" name="minus" />
          </button>
        </div>

        {/* Body */}
        <div className="px-3 py-2.5 space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-gray-500">Current type:</span>
            <span className="inline-flex items-center rounded-full bg-white border border-gray-200 px-2 py-0.5 font-medium text-gray-700">
              {adFormatLabel(currentType)}
            </span>
          </div>

          {requestMessage && (
            <div className="rounded-lg bg-white/70 border border-amber-200 px-2.5 py-1.5 text-sm text-gray-700 break-words whitespace-pre-wrap">
              {requestMessage}
            </div>
          )}

          {!accepting ? (
            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <button
                onClick={() => setAccepting(true)}
                disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-green-600 px-3 py-2 text-sm font-semibold text-white hover:bg-green-700 transition-colors disabled:opacity-60"
              >
                <SlIcon library="lucide" name="check" style={{ fontSize: "15px" }} /> Accept
              </button>
              <button
                onClick={() => setConfirmReject(true)}
                disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-white border border-red-300 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60"
              >
                <SlIcon library="lucide" name="x" style={{ fontSize: "15px" }} /> Reject
              </button>
            </div>
          ) : (
            <div className="space-y-2 pt-1">
              <label className="block text-xs font-semibold text-gray-600">Select new ad type</label>
              <Select
                options={AD_FORMAT_CHOICES}
                value={selected}
                onChange={setSelected}
                placeholder="Choose ad type…"
                classNamePrefix="react-select"
                menuPlacement="auto"
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                isDisabled={busy}
              />
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={handleAccept}
                  disabled={busy || !selected?.value}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-green-600 px-3 py-2 text-sm font-semibold text-white hover:bg-green-700 transition-colors disabled:opacity-60"
                >
                  <SlIcon library="lucide" name={busy ? "loader" : "check"} style={{ fontSize: "15px" }} />
                  {busy ? "Applying…" : "Confirm & Change"}
                </button>
                <button
                  onClick={() => { setAccepting(false); setSelected(null); }}
                  disabled={busy}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-white border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-60"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      <ConfirmationModal
        open={confirmReject}
        message={
          <span>
            Are you sure you want to <b>reject</b> this ad type change request
            {requestedByName ? <> from <b>{requestedByName}</b></> : ""}? The creative will
            return to its previous status.
          </span>
        }
        onConfirm={handleReject}
        onCancel={() => setConfirmReject(false)}
      />
    </div>
  );
};

export default AdTypeRequestBanner;
